// Первинні документи (docs/primary-documents-plan.md): види, знімок документа, підсумки й перевірки.
// Спільне для сервера (видача, хеш) і браузера (редактор, друк). Суми — тим самим ядром, що ПН (lib/pn/calc.js):
// цілі копійки, без float. Рядки — модель рядка ПН (lib/pn/model.js newRow), тож документ і ПН рахують однаково.
import { calcRow, RATES, kopUi } from "../pn/calc.js";
import { validateContract } from "./contracts.js";
import { validateSpec } from "./spec.js";
import { validateTtn } from "./ttn.js";

// Види документів. fact — який факт документ засвідчує (рахунок і договір фактів не підтверджують)
export const DOC_TYPES = {
  invoice: { title: "Рахунок на оплату", short: "Рахунок", generated: true, fact: null },
  waybill: { title: "Видаткова накладна", short: "Видаткова", generated: true, fact: "shipment", kinds: ["goods"] },
  act: { title: "Акт наданих послуг", short: "Акт", generated: true, fact: "service", kinds: ["services"] },
  contract: { title: "Договір", short: "Договір", generated: true, fact: null },
  spec: { title: "Специфікація / завдання", short: "Специфікація", generated: true, fact: null },
  ttn: { title: "Товарно-транспортна накладна", short: "ТТН", generated: true, fact: null, kinds: ["goods"] },
  other: { title: "Інший документ", short: "Інший", generated: false, fact: null },
};
export const EVENT_KINDS = {
  shipment: "Відвантаження товарів", service: "Надання послуг", payment: "Оплата",
};
export const DIRECTIONS = { sale: "Продаж", purchase: "Купівля" };
export const OP_KINDS = { goods: "Товари", services: "Послуги" };

const NO_VAT = { pct: 0n, label: "Без ПДВ" };
// Рядок документа: без ПДВ (компанія не платник) — рахуємо як звільнений рядок, підпис «Без ПДВ»
const rowFor = (row, vatPayer) => (vatPayer ? row : { ...row, rate: "903" });
export const rateLabel = (rate, vatPayer = true) => (!vatPayer ? NO_VAT.label : RATES[rate]?.label || "—");

// Рядки з назвою; порожні рядки редактора не друкуються
export const filledLines = (lines) => (lines || []).filter((r) => String(r.name || "").trim());

// Підсумки документа: по рядках і за ставками, копійки (BigInt). ok: false — є рядок, який не порахувати
export function docTotals(snap) {
  const vatPayer = snap.vatPayer !== false, mode = snap.priceMode || "net";
  const lines = [], by = {};
  let base = 0n, vatMicro = 0n, bad = [];
  for (const [i, row] of filledLines(snap.lines).entries()) {
    const c = calcRow(rowFor(row, vatPayer), mode);
    if (!c.ok) { bad.push({ i, field: c.field }); lines.push({ row, ok: false }); continue; }
    const vat = (c.vat + 5_000n) / 10_000n;
    lines.push({ row, ok: true, base: c.base, vat, gross: c.base + vat, price: c.price, qty: c.qty });
    const k = vatPayer ? row.rate : "none";
    const t = (by[k] ||= { base: 0n, vatMicro: 0n });
    t.base += c.base; t.vatMicro += c.vat;
    base += c.base; vatMicro += c.vat;
  }
  for (const t of Object.values(by)) t.vat = (t.vatMicro + 5_000n) / 10_000n;
  const vat = (vatMicro + 5_000n) / 10_000n;
  return { ok: !bad.length && lines.length > 0, bad, lines, by, base, vat, total: base + vat };
}

// Сума до оплати за рахунком: уся сума або частина (аванс) — копійки; задана більша за загальну — помилка
export function payableKop(snap, t = docTotals(snap)) {
  if (snap.type !== "invoice" || snap.payable == null || snap.payable === "") return t.total;
  const m = String(snap.payable).replace(/[\s ]/g, "").replace(",", ".").match(/^(\d+)(?:\.(\d{1,2}))?$/);
  return m ? BigInt(m[1]) * 100n + BigInt((m[2] || "").padEnd(2, "0")) : null;
}

// --- Сума словами (гривні й копійки) ----------------------------------------------------------
const ONES = [["", "один", "два", "три", "чотири", "п'ять", "шість", "сім", "вісім", "дев'ять"],
  ["", "одна", "дві", "три", "чотири", "п'ять", "шість", "сім", "вісім", "дев'ять"]];
const TEENS = ["десять", "одинадцять", "дванадцять", "тринадцять", "чотирнадцять", "п'ятнадцять", "шістнадцять", "сімнадцять", "вісімнадцять", "дев'ятнадцять"];
const TENS = ["", "", "двадцять", "тридцять", "сорок", "п'ятдесят", "шістдесят", "сімдесят", "вісімдесят", "дев'яносто"];
const HUNDREDS = ["", "сто", "двісті", "триста", "чотириста", "п'ятсот", "шістсот", "сімсот", "вісімсот", "дев'ятсот"];
// [рід (0 — ч., 1 — ж.), форми: 1, 2–4, 5+]
const SCALES = [[1, ["гривня", "гривні", "гривень"]], [1, ["тисяча", "тисячі", "тисяч"]], [0, ["мільйон", "мільйони", "мільйонів"]],
  [0, ["мільярд", "мільярди", "мільярдів"]], [0, ["трильйон", "трильйони", "трильйонів"]]];
export const plural = (n, [one, few, many]) => {
  const m10 = n % 10, m100 = n % 100;
  return m10 === 1 && m100 !== 11 ? one : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? few : many;
};
function triad(n, fem) {
  const out = [HUNDREDS[Math.floor(n / 100)]], r = n % 100;
  if (r >= 10 && r < 20) out.push(TEENS[r - 10]);
  else out.push(TENS[Math.floor(r / 10)], ONES[fem][r % 10]);
  return out.filter(Boolean).join(" ");
}
// Ціле число словами (кількість місць у ТТН): 25 → «двадцять п'ять»; fem — жіночий рід (одна, дві)
export function numberWords(n, fem = false) {
  let x = Math.floor(Math.abs(Number(n) || 0));
  if (!x) return "нуль";
  const parts = [];
  for (let i = 0; x > 0 && i < SCALES.length; i++) {
    const k = x % 1000; x = Math.floor(x / 1000);
    if (!k) continue;
    const [g, forms] = SCALES[i];
    parts.unshift(i === 0 ? triad(k, fem ? 1 : 0) : `${triad(k, g)} ${plural(k, forms)}`);
  }
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

// Копійки (BigInt чи число) → «Одна тисяча двісті гривень 50 копійок»
export function amountInWords(kop) {
  const k = BigInt(kop), neg = k < 0n, x = neg ? -k : k;
  let uah = x / 100n;
  const cents = Number(x % 100n);
  const parts = [];
  if (uah === 0n) parts.push("нуль гривень");
  for (let i = 0; uah > 0n && i < SCALES.length; i++) {
    const n = Number(uah % 1000n);
    uah /= 1000n;
    const [fem, forms] = SCALES[i];
    if (n === 0 && i > 0) continue;
    parts.unshift(`${n ? `${triad(n, fem)} ` : ""}${plural(n, forms)}`);
  }
  const s = `${neg ? "мінус " : ""}${parts.join(" ")} ${String(cents).padStart(2, "0")} ${plural(cents, ["копійка", "копійки", "копійок"])}`.replace(/\s+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// --- Перевірки знімка перед видачею ----------------------------------------------------------------
// Повертає [{ path, message }]; порожньо — можна видавати. Невідомі факти не вигадуємо: дата відвантаження
// чи період послуг — лише введені користувачем
const ISO = /^\d{4}-\d{2}-\d{2}$/;
export function validateDoc(snap) {
  if (snap.type === "contract") return validateContract(snap);
  if (snap.type === "spec") return validateSpec(snap);
  if (snap.type === "ttn") return validateTtn(snap);
  const e = [], add = (path, message) => e.push({ path, message });
  if (!String(snap.number || "").trim()) add("number", "Вкажіть номер документа");
  if (!ISO.test(snap.date || "")) add("date", "Вкажіть дату складання");
  for (const [side, label] of [["seller", snap.type === "act" ? "виконавця" : "постачальника"], ["buyer", snap.type === "act" ? "замовника" : "покупця"]]) {
    const p = snap[side] || {};
    if (!String(p.name || "").trim()) add(`${side}.name`, `Вкажіть найменування ${label}`);
    if (!/^(\d{8}|\d{10})$/.test(String(p.tin || "").trim())) add(`${side}.tin`, `Код ЄДРПОУ чи РНОКПП ${label}: 8 або 10 цифр`);
  }
  if (snap.type === "invoice" && !/^UA\d{27}$/.test(String(snap.seller?.iban || "").replace(/\s/g, ""))) add("seller.iban", "Рахунок на оплату: вкажіть IBAN отримувача (UA і 27 цифр)");
  if (snap.type === "waybill" && !ISO.test(snap.shipmentDate || "")) add("shipmentDate", "Вкажіть фактичну дату відвантаження");
  if (snap.type === "act") {
    if (!ISO.test(snap.periodFrom || "") || !ISO.test(snap.periodTo || "")) add("periodFrom", "Вкажіть фактичний період надання послуг");
    else if (snap.periodFrom > snap.periodTo) add("periodTo", "Кінець періоду раніше за початок");
  }
  const t = docTotals(snap);
  if (!filledLines(snap.lines).length) add("lines", "Додайте хоча б одну позицію");
  for (const b of t.bad) add(`lines.${b.i}.${b.field}`, `Позиція ${b.i + 1}: ${{ qty: "кількість", price: "ціна", sum: "сума", rate: "ставка ПДВ" }[b.field] || b.field} — не заповнено чи некоректно`);
  if (snap.type === "invoice" && snap.payable != null && snap.payable !== "") {
    const p = payableKop(snap, t);
    if (p == null || p <= 0n) add("payable", "Сума до оплати: число з копійками, більше нуля");
    else if (t.ok && p > t.total) add("payable", `Сума до оплати більша за суму позицій (${kopUi(t.total)} грн)`);
  }
  return e;
}

// Канонічний JSON (ключі за абеткою) — для хешу виданої версії: однаковий зміст — однаковий хеш
export function canonical(v) {
  if (Array.isArray(v)) return `[${v.map(canonical).join(",")}]`;
  if (v && typeof v === "object") return `{${Object.keys(v).sort().filter((k) => v[k] !== undefined).map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(",")}}`;
  return JSON.stringify(v ?? null);
}
